import { Vec2 } from '../math/vec2.js';
import { ARENA_WIDTH, ARENA_HEIGHT, NPC_DECISION_INTERVAL, NPC_TURN_IN_THRESHOLD, NPC_ATTACK_RANGE, NPC_MINING_RANGE, NPC_COLLECT_RANGE, NPC_ACCURACY_BASE, STATION_TURN_IN_RADIUS, } from '../constants/game.js';
/**
 * Generate input for all NPC ships
 * Returns a map of playerId -> InputState for NPCs only
 */
export function generateNpcInputs(world, dt) {
    const inputs = new Map();
    for (const ship of world.ships) {
        if (!ship.isNpc || !ship.npcAi || !ship.alive)
            continue;
        const ai = ship.npcAi;
        // Update decision cooldown
        ai.decisionCooldown -= dt;
        ai.stateTimer += dt;
        // Make decisions periodically
        if (ai.decisionCooldown <= 0) {
            updateNpcState(world, ship);
            ai.decisionCooldown = NPC_DECISION_INTERVAL * (0.8 + Math.random() * 0.4);
        }
        // Generate input based on current state
        const input = generateInputForState(world, ship);
        inputs.set(ship.playerId, input);
    }
    return inputs;
}
/**
 * Update NPC state machine - decide what to do next
 */
function updateNpcState(world, ship) {
    const ai = ship.npcAi;
    const tailLength = ship.tailSegments.length;
    // Priority 1: Turn in if we have enough crates
    if (tailLength >= NPC_TURN_IN_THRESHOLD) {
        ai.state = 'turning_in';
        ai.targetPos = world.station.pos.clone();
        ai.targetId = null;
        return;
    }
    // Priority 2: Check for nearby threats (aggressive players)
    const nearbyThreat = findNearbyThreat(world, ship);
    if (nearbyThreat && tailLength > 0) {
        // Decide: fight or flight based on aggressiveness and cargo
        const shouldFlee = tailLength > 3 || Math.random() > ai.aggressiveness;
        if (shouldFlee) {
            ai.state = 'fleeing';
            ai.targetId = nearbyThreat.id;
            ai.targetPos = nearbyThreat.pos.clone();
        }
        else {
            ai.state = 'attacking';
            ai.targetId = nearbyThreat.id;
            ai.targetPos = nearbyThreat.pos.clone();
        }
        return;
    }
    // Priority 3: Collect nearby loose crates
    const nearestCrate = findNearestCrate(world, ship);
    if (nearestCrate) {
        ai.state = 'collecting';
        ai.targetId = nearestCrate.id;
        ai.targetPos = nearestCrate.pos.clone();
        return;
    }
    // Priority 4: Attack a juicy target (player with lots of crates)
    const juicyTarget = findJuicyTarget(world, ship);
    if (juicyTarget && Math.random() < ai.aggressiveness * 0.5) {
        ai.state = 'attacking';
        ai.targetId = juicyTarget.id;
        ai.targetPos = juicyTarget.pos.clone();
        return;
    }
    // Default: Mine asteroids
    const nearestAsteroid = findNearestAsteroid(world, ship);
    if (nearestAsteroid) {
        ai.state = 'mining';
        ai.targetId = nearestAsteroid.id;
        ai.targetPos = nearestAsteroid.pos.clone();
    }
    else {
        // No asteroids? Wander toward center
        ai.state = 'mining';
        ai.targetId = null;
        ai.targetPos = new Vec2(ARENA_WIDTH / 2, ARENA_HEIGHT / 2);
    }
}
/**
 * Generate input based on current AI state
 */
function generateInputForState(world, ship) {
    const ai = ship.npcAi;
    switch (ai.state) {
        case 'mining':
            return handleMiningState(world, ship);
        case 'collecting':
            return handleCollectingState(world, ship);
        case 'turning_in':
            return handleTurningInState(world, ship);
        case 'fleeing':
            return handleFleeingState(world, ship);
        case 'attacking':
            return handleAttackingState(world, ship);
        default:
            return createIdleInput();
    }
}
/**
 * Mining: Navigate to asteroid and shoot it
 */
function handleMiningState(world, ship) {
    const ai = ship.npcAi;
    // Find the target asteroid
    const asteroid = world.asteroids.find((a) => a.id === ai.targetId);
    if (!asteroid) {
        // Target gone, will re-evaluate next decision
        return createIdleInput();
    }
    // Update target position
    ai.targetPos = asteroid.pos.clone();
    const toTarget = asteroid.pos.sub(ship.pos);
    const distance = toTarget.length();
    const targetAngle = toTarget.angle();
    // Navigate toward asteroid
    const nav = calculateNavigation(ship, targetAngle, distance);
    // Shoot when facing the asteroid and in range
    const angleDiff = normalizeAngle(targetAngle - ship.rot);
    const shouldShoot = Math.abs(angleDiff) < 0.3 && distance < NPC_MINING_RANGE;
    return {
        thrust: nav.thrust,
        rotation: nav.rotation,
        aimAngle: targetAngle,
        shooting: shouldShoot,
    };
}
/**
 * Collecting: Navigate to crate and pick it up
 */
function handleCollectingState(world, ship) {
    const ai = ship.npcAi;
    // Find the target crate
    const crate = world.crates.find((c) => c.id === ai.targetId);
    if (!crate) {
        // Crate gone, will re-evaluate
        return createIdleInput();
    }
    ai.targetPos = crate.pos.clone();
    const toTarget = crate.pos.sub(ship.pos);
    const distance = toTarget.length();
    const targetAngle = toTarget.angle();
    const nav = calculateNavigation(ship, targetAngle, distance);
    return {
        thrust: nav.thrust,
        rotation: nav.rotation,
        aimAngle: targetAngle,
        shooting: false,
    };
}
/**
 * Turning in: Navigate to station
 */
function handleTurningInState(world, ship) {
    const ai = ship.npcAi;
    ai.targetPos = world.station.pos.clone();
    const toStation = world.station.pos.sub(ship.pos);
    const distance = toStation.length();
    const targetAngle = toStation.angle();
    // If we're in turn-in range, we'll auto turn in via the system
    if (distance < STATION_TURN_IN_RADIUS) {
        return createIdleInput();
    }
    const nav = calculateNavigation(ship, targetAngle, distance);
    return {
        thrust: nav.thrust,
        rotation: nav.rotation,
        aimAngle: targetAngle,
        shooting: false,
    };
}
/**
 * Fleeing: Run away from threat
 */
function handleFleeingState(world, ship) {
    const ai = ship.npcAi;
    // Find the threat
    const threat = world.ships.find((s) => s.id === ai.targetId);
    if (!threat || !threat.alive) {
        return createIdleInput();
    }
    // Run in opposite direction
    const awayFromThreat = ship.pos.sub(threat.pos);
    const fleeAngle = awayFromThreat.angle();
    const nav = calculateNavigation(ship, fleeAngle, 1000); // Always thrust
    // Maybe shoot behind us if threatened
    const toThreat = threat.pos.sub(ship.pos);
    const threatAngle = toThreat.angle();
    const angleDiff = normalizeAngle(threatAngle - ship.rot);
    const shouldShoot = Math.abs(angleDiff) < 0.4 && toThreat.length() < 200;
    return {
        thrust: nav.thrust,
        rotation: nav.rotation,
        aimAngle: fleeAngle,
        shooting: shouldShoot,
    };
}
/**
 * Attacking: Chase and shoot target
 */
function handleAttackingState(world, ship) {
    const ai = ship.npcAi;
    // Find the target
    const target = world.ships.find((s) => s.id === ai.targetId);
    if (!target || !target.alive) {
        return createIdleInput();
    }
    ai.targetPos = target.pos.clone();
    // Lead the target based on their velocity
    const leadTime = 0.3 * ai.skillLevel;
    const predictedPos = target.pos.add(target.vel.scale(leadTime));
    const toTarget = predictedPos.sub(ship.pos);
    const distance = toTarget.length();
    const targetAngle = toTarget.angle();
    // Add some inaccuracy based on skill
    const accuracy = NPC_ACCURACY_BASE + (1 - NPC_ACCURACY_BASE) * ai.skillLevel;
    const aimError = (1 - accuracy) * (Math.random() - 0.5) * 0.5;
    const aimAngle = targetAngle + aimError;
    const nav = calculateNavigation(ship, aimAngle, distance);
    // Shoot when reasonably aimed
    const angleDiff = normalizeAngle(targetAngle - ship.rot);
    const shouldShoot = Math.abs(angleDiff) < 0.4 && distance < NPC_ATTACK_RANGE;
    return {
        thrust: nav.thrust,
        rotation: nav.rotation,
        aimAngle: aimAngle,
        shooting: shouldShoot,
    };
}
/**
 * Calculate navigation inputs to face and move toward a target angle
 */
function calculateNavigation(ship, targetAngle, distance) {
    const angleDiff = normalizeAngle(targetAngle - ship.rot);
    // Rotation: turn toward target
    let rotation = 0;
    if (angleDiff > 0.1)
        rotation = 1;
    else if (angleDiff < -0.1)
        rotation = -1;
    // Thrust: move forward when facing roughly the right direction
    let thrust = 0;
    if (Math.abs(angleDiff) < Math.PI / 2 && distance > 30) {
        thrust = 1;
    }
    return { thrust, rotation };
}
/**
 * Find nearest asteroid within range
 */
function findNearestAsteroid(world, ship) {
    let nearest = null;
    for (const asteroid of world.asteroids) {
        const dist = ship.pos.distanceTo(asteroid.pos);
        if (dist < NPC_MINING_RANGE && (!nearest || dist < nearest.dist)) {
            nearest = { id: asteroid.id, pos: asteroid.pos, dist };
        }
    }
    // If nothing in range, find closest anywhere
    if (!nearest) {
        for (const asteroid of world.asteroids) {
            const dist = ship.pos.distanceTo(asteroid.pos);
            if (!nearest || dist < nearest.dist) {
                nearest = { id: asteroid.id, pos: asteroid.pos, dist };
            }
        }
    }
    return nearest ? { id: nearest.id, pos: nearest.pos } : null;
}
/**
 * Find nearest loose crate within range
 */
function findNearestCrate(world, ship) {
    let nearest = null;
    for (const crate of world.crates) {
        // Skip crates on cooldown from this ship
        if (crate.pickupCooldown > 0 && crate.lastOwnerId === ship.playerId)
            continue;
        const dist = ship.pos.distanceTo(crate.pos);
        if (dist < NPC_COLLECT_RANGE && (!nearest || dist < nearest.dist)) {
            nearest = { id: crate.id, pos: crate.pos, dist };
        }
    }
    return nearest ? { id: nearest.id, pos: nearest.pos } : null;
}
/**
 * Find nearby threat (ship shooting at us or very close)
 */
function findNearbyThreat(world, ship) {
    for (const other of world.ships) {
        if (other.playerId === ship.playerId || !other.alive)
            continue;
        const dist = ship.pos.distanceTo(other.pos);
        // Consider a threat if very close
        if (dist < 150) {
            return { id: other.id, pos: other.pos };
        }
        // Check if any projectiles from this ship are heading toward us
        for (const proj of world.projectiles) {
            if (proj.ownerId === other.playerId) {
                const projDist = ship.pos.distanceTo(proj.pos);
                if (projDist < 200) {
                    return { id: other.id, pos: other.pos };
                }
            }
        }
    }
    return null;
}
/**
 * Find a player with lots of crates (juicy target)
 */
function findJuicyTarget(world, ship) {
    let best = null;
    for (const other of world.ships) {
        if (other.playerId === ship.playerId || !other.alive)
            continue;
        if (other.tailSegments.length < 3)
            continue; // Not worth it
        const dist = ship.pos.distanceTo(other.pos);
        if (dist > NPC_ATTACK_RANGE * 1.5)
            continue; // Too far
        // Score based on tail length and proximity
        const score = other.tailSegments.length * 10 - dist * 0.1;
        if (!best || score > best.score) {
            best = { id: other.id, pos: other.pos, score };
        }
    }
    return best ? { id: best.id, pos: best.pos } : null;
}
/**
 * Normalize angle to [-PI, PI]
 */
function normalizeAngle(angle) {
    while (angle > Math.PI)
        angle -= Math.PI * 2;
    while (angle < -Math.PI)
        angle += Math.PI * 2;
    return angle;
}
/**
 * Create idle input (no movement)
 */
function createIdleInput() {
    return { thrust: 0, rotation: 0, aimAngle: 0, shooting: false };
}
//# sourceMappingURL=npc-ai.js.map